/**
 * Gameplay rules GP-01 ... GP-10 (W2-12b checker-rules; DESIGN.md §7.6; rpe-editor-features.md E§3.16 AC-04/AC-05;
 * rpe-format.md F§11.4, F§11.6, F§11.8; rendering.md R§1.2, R§4.5).
 *
 * Thresholds come from Settings > Checker (`CheckContext.thresholds`, RPE A.C. defaults: X 700, collision 200 px,
 * read time 0.3 s, drag window 0.04 s, combo window 0.16 s, near-simultaneous 2 ms).
 *
 * Judge geometry follows Phira (F§11.6): a touch is transformed into the note's line frame and only the x distance
 * along that line counts, using the RAW positionX (posControl and incline move the drawn note, not its hit area).
 * A touch sliding through one note therefore reaches another note of any line whose judge band it crosses (GP-01,
 * GP-02 measure that judge distance, in the frame of the note being judged, at the moment of the touch). Overlaps
 * (GP-03) and notes on a hold's ends (GP-07) are about notes players see on the same spot, so they measure the
 * on-screen distance between the hit points. Positions use the reference viewport's effective aspect ratio
 * (1920 x 1080 with the Phira player settings, R§1.2). Fake notes are not judged and are ignored, except by GP-08 (they
 * are drawn at their X like any note) and GP-09 (simultaneous highlights count fake notes, F§11.8).
 *
 * Incremental passes (UX-092): the pair rules GP-01, GP-02, GP-03, GP-07 and GP-09 are `WindowedCheckRule`s: they
 * read notes through `notesByTime` / `realNotesByTime`, anchor every problem at `at.sec` and look no further than
 * `windowPadSec` from it, so after a note edit the runner re-checks only the pairs around the edited notes.
 *
 * Worker-safe: imports only src/core/**.
 */

import {
  FIELD,
  SAME_TIME_EPS_SEC,
  WINDOW_EPS_SEC,
  beatArg,
  effectiveAspect,
  emitProblem,
  firstAtOrAfter,
  fixRef,
  fmtBeat,
  fmtNum,
  forEachPairWithin,
  frameToWorld,
  isOnScreen,
  judgeBandCrossesScreen,
  judgeDistancePx,
  layerOf,
  lineFrameAt,
  lineLabel,
  lineNotes,
  linesToCheck,
  msOf,
  msg,
  musicLengthSec,
  navFix,
  notesByTime,
  offsetSec,
  pairKey,
  problemId,
  propTracks,
  readTimeSec,
  realNotesByTime,
  revealFix,
  screenDistancePx,
  trackKeys,
  RPE_X_PER_WORLD,
  type TimedNote,
  type WindowedCheckRule,
} from '../../../core/check/geometry';
import type { CheckContext, CheckRule, FixRef } from '../../../core/check/types';
import type { Key, KeyId, LineId, Note, NoteId, Track } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import type { Beat } from '../../../core/time/types';

function label(ctx: CheckContext, line: LineId): string {
  return lineLabel(ctx.doc, ctx.idx, line);
}

function selectBoth(a: TimedNote, b: TimedNote): FixRef {
  return revealFix(msg('rules.fixSelectBoth'), b.note.t, { line: b.note.line, notes: [a.id, b.id] });
}

function selectNotes(notes: readonly Note[]): FixRef {
  const first = notes[0];
  return revealFix(
    msg('rules.fixSelectNotes', { count: notes.length }),
    first?.t ?? Beats.ZERO,
    first === undefined ? {} : { line: first.line, notes: notes.map((n) => n.id) },
  );
}

/** Real notes of a line with their Phira seconds, in time order. */
function timedLineNotes(ctx: CheckContext, line: LineId): TimedNote[] {
  return lineNotes(ctx, line)
    .filter((n) => !n.fake)
    .map((note) => {
      const sec = ctx.engine.noteSec(note.id);
      return { id: note.id, note, sec, endSec: note.kind === 'hold' ? ctx.engine.noteEndSec(note.id) : sec };
    })
    .sort((a, b) => a.sec - b.sec || a.id - b.id);
}

/** Tap after a nearby note in a time window: the shared body of GP-01 and GP-02. */
function tapsAfter(
  ctx: CheckContext,
  before: 'flick' | 'drag',
  visit: (prev: TimedNote, tap: TimedNote, dt: number, px: number) => void,
): void {
  const { dragWindowSec, comboWindowSec, collisionPx } = ctx.thresholds;
  const maxDt = before === 'flick' ? comboWindowSec : Math.max(dragWindowSec, comboWindowSec);
  const ar = effectiveAspect(ctx);
  forEachPairWithin(realNotesByTime(ctx), maxDt, (a, b, dt) => {
    if (a.note.kind !== before || b.note.kind !== 'tap' || dt <= SAME_TIME_EPS_SEC) return;
    // The touch that slides through the earlier note lands on the tap while the earlier note is judged.
    const px = judgeDistancePx(ctx.engine, a.note, b.note, a.sec, ar);
    if (px < collisionPx) visit(a, b, dt, px);
  });
}

// ---------------------------------------------------------------------------------------------------------------
// GP-01 tap shortly after a nearby flick
// ---------------------------------------------------------------------------------------------------------------

/** Reach of GP-01 / GP-02: the later of the two windows, plus the comparison tolerance. */
function tapAfterPad(ctx: CheckContext): number {
  const { dragWindowSec, comboWindowSec } = ctx.thresholds;
  return Math.max(dragWindowSec, comboWindowSec) + WINDOW_EPS_SEC;
}

const gp01: WindowedCheckRule = {
  id: 'GP-01',
  category: 'gameplay',
  severity: 'error',
  level: 'guided',
  scope: 'chart',
  deps: ['notes', 'motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp01Title'),
  windowPadSec: tapAfterPad,
  check(ctx) {
    const { dragWindowSec, comboWindowSec } = ctx.thresholds;
    tapsAfter(ctx, 'flick', (flick, tap, dt, px) => {
      if (dt < dragWindowSec - WINDOW_EPS_SEC || dt > comboWindowSec + WINDOW_EPS_SEC) return;
      emitProblem(ctx, {
        id: problemId('GP-01', 'pair', `${String(flick.id)}-${String(tap.id)}`),
        message: msg('rules.gp01What', { ms: msOf(dt), px: Math.round(px) }),
        why: msg('rules.gp01Why'),
        at: {
          line: tap.note.line,
          beat: tap.note.t,
          sec: tap.sec,
          x: tap.note.x,
          notes: [flick.id, tap.id],
          field: FIELD.noteTime,
        },
        fixes: [
          fixRef('problems.fix.moveNotesLater', msg('rules.fixMoveTapLater'), { notes: [tap.id] }),
          selectBoth(flick, tap),
        ],
      });
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-02 tap shortly after a nearby drag
// ---------------------------------------------------------------------------------------------------------------

const gp02: WindowedCheckRule = {
  id: 'GP-02',
  category: 'gameplay',
  severity: 'warning',
  level: 'guided',
  scope: 'chart',
  deps: ['notes', 'motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp02Title'),
  windowPadSec: tapAfterPad,
  check(ctx) {
    const { dragWindowSec, comboWindowSec } = ctx.thresholds;
    tapsAfter(ctx, 'drag', (drag, tap, dt, px) => {
      const close = dt <= dragWindowSec + WINDOW_EPS_SEC;
      if (!close && dt > comboWindowSec + WINDOW_EPS_SEC) return;
      emitProblem(ctx, {
        id: problemId('GP-02', 'pair', `${String(drag.id)}-${String(tap.id)}`),
        ...(close ? {} : { severity: 'tip' as const }),
        message: msg('rules.gp02What', { ms: msOf(dt), px: Math.round(px) }),
        why: msg(close ? 'rules.gp02Why' : 'rules.gp02WhyLater'),
        at: {
          line: tap.note.line,
          beat: tap.note.t,
          sec: tap.sec,
          x: tap.note.x,
          notes: [drag.id, tap.id],
          field: FIELD.noteTime,
        },
        fixes: [
          fixRef('problems.fix.moveNotesLater', msg('rules.fixMoveTapLater'), { notes: [tap.id] }),
          selectBoth(drag, tap),
        ],
      });
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-03 overlapping notes at the same time
// ---------------------------------------------------------------------------------------------------------------

function isDuplicate(a: Note, b: Note): boolean {
  return (
    a.line === b.line &&
    a.kind === b.kind &&
    Math.abs(a.x - b.x) < 1e-6 &&
    Beats.eq(a.t, b.t) &&
    Beats.eq(a.end, b.end) &&
    a.above === b.above
  );
}

const gp03: WindowedCheckRule = {
  id: 'GP-03',
  category: 'gameplay',
  severity: 'error',
  level: 'guided',
  scope: 'chart',
  deps: ['notes', 'motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp03Title'),
  windowPadSec: () => SAME_TIME_EPS_SEC + WINDOW_EPS_SEC,
  check(ctx) {
    const ar = effectiveAspect(ctx);
    forEachPairWithin(realNotesByTime(ctx), SAME_TIME_EPS_SEC, (a, b) => {
      const ka = a.note.kind;
      const kb = b.note.kind;
      if ((ka !== 'tap' && ka !== 'hold') || (kb !== 'tap' && kb !== 'hold')) return;
      // Players see one note on top of the other and use one finger, which judges only one of them.
      const px = screenDistancePx(ctx.engine, a.note, b.note, a.sec, ar);
      if (px >= ctx.thresholds.collisionPx) return;
      const pair = ka === kb ? `${ka}${kb}` : 'taphold';
      const fixes: FixRef[] = [];
      if (isDuplicate(a.note, b.note)) {
        fixes.push(fixRef('problems.fix.deleteNotes', msg('rules.fixDeleteDuplicate'), { notes: [b.id] }));
      }
      fixes.push(selectBoth(a, b));
      emitProblem(ctx, {
        id: problemId('GP-03', 'pair', pairKey(a.id, b.id)),
        message: msg('rules.gp03What', { pair, beat: fmtBeat(a.note.t), px: Math.round(px) }),
        why: msg('rules.gp03Why'),
        at: { line: b.note.line, beat: b.note.t, sec: b.sec, x: b.note.x, notes: [a.id, b.id], field: FIELD.noteX },
        fixes,
      });
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-04 notes before the song starts or after it ends
// ---------------------------------------------------------------------------------------------------------------

const gp04: CheckRule = {
  id: 'GP-04',
  category: 'gameplay',
  severity: 'error',
  level: 'guided',
  scope: 'line',
  deps: ['notes', 'tempo', 'timing', 'meta', 'assets', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.gp04Title'),
  check(ctx, only) {
    const off = offsetSec(ctx.doc);
    const len = musicLengthSec(ctx);
    for (const line of linesToCheck(ctx, only)) {
      const before: Note[] = [];
      const after: Note[] = [];
      for (const n of timedLineNotes(ctx, line)) {
        if (n.sec + off < -SAME_TIME_EPS_SEC) before.push(n.note);
        else if (len !== null && n.endSec + off > len + SAME_TIME_EPS_SEC) after.push(n.note);
      }
      for (const [which, notes] of [
        ['before', before],
        ['after', after],
      ] as const) {
        const first = notes[0];
        if (first === undefined) continue;
        emitProblem(ctx, {
          id: problemId('GP-04', 'line', `${String(line)}-${which}`),
          message: msg(which === 'before' ? 'rules.gp04Before' : 'rules.gp04After', {
            count: notes.length,
            line: label(ctx, line),
            beat: fmtBeat(first.t),
          }),
          why: msg(which === 'before' ? 'rules.gp04BeforeWhy' : 'rules.gp04AfterWhy', {
            offset: Math.round(off * 1000),
          }),
          at: { line, beat: first.t, notes: notes.map((n) => n.id), field: FIELD.noteTime },
          fixes: [selectNotes(notes)],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-05 note judged off screen
// ---------------------------------------------------------------------------------------------------------------

const gp05: CheckRule = {
  id: 'GP-05',
  category: 'gameplay',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['notes', 'motion', 'tempo', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.gp05Title'),
  check(ctx, only) {
    const ar = effectiveAspect(ctx);
    for (const line of linesToCheck(ctx, only)) {
      const off: Note[] = [];
      let unreachable = 0;
      for (const n of timedLineNotes(ctx, line)) {
        const frame = lineFrameAt(ctx.engine, line, n.sec, ar);
        const xLocal = n.note.x / RPE_X_PER_WORLD;
        if (isOnScreen(frameToWorld(frame, xLocal, 0), ar)) continue;
        off.push(n.note);
        if (!judgeBandCrossesScreen(frame, xLocal, ar)) unreachable++;
      }
      const first = off[0];
      if (first === undefined) continue;
      emitProblem(ctx, {
        id: problemId('GP-05', 'line', line),
        message: msg('rules.gp05What', { count: off.length, line: label(ctx, line), beat: fmtBeat(first.t) }),
        why: unreachable > 0 ? msg('rules.gp05WhyUnreachable', { count: unreachable }) : msg('rules.gp05WhyReachable'),
        at: { line, beat: first.t, x: first.x, notes: off.map((n) => n.id), field: FIELD.noteX },
        fixes: [selectNotes(off)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-06 short read time
// ---------------------------------------------------------------------------------------------------------------

/** Notes of one line hit at the same beat whose read time is short (or none): one GP-06 problem (UX-093). */
interface ShortRead {
  readonly notes: Note[];
  /** Shortest read time of the group (null: none of them is visible before its hit). */
  read: number | null;
}

const gp06: CheckRule = {
  id: 'GP-06',
  category: 'gameplay',
  severity: 'tip',
  level: 'guided',
  scope: 'line',
  deps: ['notes', 'motion', 'tempo', 'lines', 'pkg'],
  defaultEnabled: true,
  title: msg('rules.gp06Title'),
  check(ctx, only) {
    const min = ctx.thresholds.readTimeSec;
    for (const line of linesToCheck(ctx, only)) {
      // Notes of UI-mover lines are never drawn (PH-12 reports them).
      if (ctx.doc.lines.get(line)?.attachUI != null) continue;
      // The notes of a chord share one problem (one row instead of one per note), visible ones apart from hidden ones.
      const groups = new Map<string, ShortRead>();
      for (const note of lineNotes(ctx, line)) {
        if (note.fake || note.alpha <= 0) continue;
        // Plain notes with negative speed are culled by Phira (PH-13 reports them).
        if (note.kind !== 'hold' && note.speed < 0 && ctx.player.aggressiveCull) continue;
        // Notes hit while the line's opacity is below 0 are hidden by rule (PH-23 reports them).
        if (ctx.engine.lineState(line, ctx.engine.noteSec(note.id)).alpha < 0) continue;
        const read = readTimeSec(ctx, note.id, min);
        if (read !== null && read + 1e-9 >= min) continue;
        const key = `${fmtBeat(note.t)}|${read === null ? 'never' : 'short'}`;
        const g = groups.get(key);
        if (g === undefined) groups.set(key, { notes: [note], read });
        else {
          g.notes.push(note);
          if (read !== null && g.read !== null) g.read = Math.min(g.read, read);
        }
      }
      for (const g of groups.values()) {
        const first = g.notes.reduce((a, b) => (b.id < a.id ? b : a));
        const count = g.notes.length;
        const kinds = new Set(g.notes.map((n) => n.kind));
        const kind = kinds.size === 1 ? first.kind : 'mixed';
        const base = { count, kind, line: label(ctx, line), beat: fmtBeat(first.t) };
        emitProblem(ctx, {
          id: problemId('GP-06', 'note', first.id),
          message:
            g.read === null ? msg('rules.gp06Never', base) : msg('rules.gp06What', { ...base, ms: msOf(g.read) }),
          why: msg('rules.gp06Why', { min: msOf(min), w: ctx.refViewport.w, h: ctx.refViewport.h }),
          at: { line, beat: first.t, x: first.x, notes: g.notes.map((n) => n.id), field: FIELD.noteTime },
          fixes: [revealFix(msg('rules.fixGoTo'), first.t, { line, notes: g.notes.map((n) => n.id) })],
        });
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-07 hold touching another note at its start or end
// ---------------------------------------------------------------------------------------------------------------

const gp07: WindowedCheckRule = {
  id: 'GP-07',
  category: 'gameplay',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['notes', 'motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp07Title'),
  // A partner within the near-simultaneous window of the hold's start or end (the hold's span reaches the window).
  windowPadSec: (ctx) => ctx.thresholds.nearSimultaneousSec + SAME_TIME_EPS_SEC,
  check(ctx) {
    const list = realNotesByTime(ctx);
    const w = ctx.thresholds.nearSimultaneousSec;
    const ar = effectiveAspect(ctx);
    for (const hold of list) {
      if (hold.note.kind !== 'hold') continue;
      for (const end of ['start', 'end'] as const) {
        const t = end === 'start' ? hold.sec : hold.endSec;
        if (end === 'end' && t - hold.sec <= SAME_TIME_EPS_SEC) continue;
        for (let i = firstAtOrAfter(list, t - w - SAME_TIME_EPS_SEC); i < list.length; i++) {
          const other = list[i];
          if (other === undefined || other.sec > t + w + SAME_TIME_EPS_SEC) break;
          if (other.id === hold.id) continue;
          const dt = Math.abs(other.sec - t);
          // Exactly simultaneous taps and holds at a hold's start are overlaps (GP-03).
          if (end === 'start' && dt <= SAME_TIME_EPS_SEC && (other.note.kind === 'tap' || other.note.kind === 'hold')) {
            continue;
          }
          const px = screenDistancePx(ctx.engine, hold.note, other.note, t, ar);
          if (px >= ctx.thresholds.collisionPx) continue;
          emitProblem(ctx, {
            id: problemId('GP-07', 'pair', `${String(hold.id)}-${String(other.id)}-${end}`),
            message: msg(end === 'start' ? 'rules.gp07Start' : 'rules.gp07End', {
              kind: other.note.kind,
              beat: fmtBeat(other.note.t),
              px: Math.round(px),
            }),
            why: msg(end === 'start' ? 'rules.gp07StartWhy' : 'rules.gp07EndWhy'),
            at: {
              line: other.note.line,
              beat: other.note.t,
              sec: other.sec,
              x: other.note.x,
              notes: [hold.id, other.id],
              field: FIELD.noteTime,
            },
            fixes: [selectBoth(hold, other)],
          });
        }
      }
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-08 |X| beyond the threshold
// ---------------------------------------------------------------------------------------------------------------

const gp08: CheckRule = {
  id: 'GP-08',
  category: 'gameplay',
  severity: 'tip',
  level: 'guided',
  scope: 'line',
  deps: ['notes'],
  defaultEnabled: true,
  title: msg('rules.gp08Title'),
  check(ctx, only) {
    const max = ctx.thresholds.xMax;
    for (const line of linesToCheck(ctx, only)) {
      // Decoration (fake) notes count too (RPE's "X Too Large" has no exception): they are drawn at that X as well.
      const far = lineNotes(ctx, line).filter((n) => Math.abs(n.x) > max);
      const first = far[0];
      if (first === undefined) continue;
      const ids: NoteId[] = far.map((n) => n.id);
      emitProblem(ctx, {
        id: problemId('GP-08', 'line', line),
        message: msg('rules.gp08What', {
          count: far.length,
          line: label(ctx, line),
          max,
          beat: fmtBeat(first.t),
          x: fmtNum(first.x, 1),
        }),
        why: far.every((n) => n.fake) ? msg('rules.gp08WhyFake') : msg('rules.gp08Why'),
        at: { line, beat: first.t, x: first.x, notes: ids, field: FIELD.noteX },
        fixes: [fixRef('problems.fix.snapNotesX', msg('rules.fixSnapX'), { notes: ids }), selectNotes(far)],
      });
    }
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-09 almost-simultaneous notes
// ---------------------------------------------------------------------------------------------------------------

const gp09: WindowedCheckRule = {
  id: 'GP-09',
  category: 'gameplay',
  severity: 'tip',
  level: 'guided',
  scope: 'chart',
  deps: ['notes', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp09Title'),
  windowPadSec: (ctx) => ctx.thresholds.nearSimultaneousSec + WINDOW_EPS_SEC,
  check(ctx) {
    const w = ctx.thresholds.nearSimultaneousSec;
    // Multi-highlight compares exact f64 times and counts fake notes and hold heads (F§11.8).
    forEachPairWithin(notesByTime(ctx), w, (a, b, dt) => {
      if (dt === 0 || dt >= w) return;
      emitProblem(ctx, {
        id: problemId('GP-09', 'pair', pairKey(a.id, b.id)),
        message: msg('rules.gp09What', { beat: fmtBeat(a.note.t), ms: fmtNum(dt * 1000, 3) }),
        why: msg('rules.gp09Why'),
        at: { line: b.note.line, beat: b.note.t, sec: b.sec, notes: [a.id, b.id], field: FIELD.noteTime },
        fixes: [
          fixRef('problems.fix.alignNoteTimes', msg('rules.fixSnapTogether', { beat: fmtBeat(a.note.t) }), {
            notes: [b.id],
            beat: beatArg(a.note.t),
          }),
          selectBoth(a, b),
        ],
      });
    });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// GP-10 opacity outside 0..255 (negatives that hide notes are intentional)
// ---------------------------------------------------------------------------------------------------------------

const ALPHA_MAX = 255;

interface AlphaLayer {
  readonly track: Track;
  readonly layer: number;
  readonly keys: readonly Key[];
}

/** Opacity layers of a line that have keyframes, by layer. */
function alphaLayers(ctx: CheckContext, line: LineId): AlphaLayer[] {
  return propTracks(ctx, line, 'alpha')
    .map((track) => ({ track, layer: layerOf(track), keys: trackKeys(ctx, track.id) }))
    .filter((l) => l.keys.length > 0);
}

/** A keyframe time at which the summed opacity of a multi-layer line exceeds 255. */
interface SumOver {
  readonly beat: Beat;
  /** Largest of the totals arriving at and leaving the keyframe time. */
  readonly value: number;
  /** Keyframes of any layer at that time. */
  readonly keys: readonly Key[];
}

/**
 * Keyframe times of a multi-layer line at which the layers add up to more than 255. Phira draws the SUM of the
 * layers (F§8), so one layer above 255 is fine while another cancels it, and two layers of 200 are not. At every
 * keyframe time of any layer the total is taken on both sides (a jump arrives at one value and leaves with another):
 * a layer with a keyframe there contributes that keyframe's values, the others their value at that time as Phira
 * evaluates it (eased, extrapolated before their first keyframe; `Engine.channelValue`).
 */
function summedOver(ctx: CheckContext, line: LineId, layers: readonly AlphaLayer[]): SumOver[] {
  const all = layers.flatMap((l) => l.keys.map((key) => ({ key, layer: l.layer })));
  all.sort((a, b) => Beats.cmp(a.key.t, b.key.t));
  const out: SumOver[] = [];
  for (let i = 0; i < all.length;) {
    const head = all[i];
    if (head === undefined) break;
    const beat = head.key.t;
    // First and last keyframe of each layer at this time (a layer may hold two keyframes at one beat).
    const here = new Map<number, { first: Key; last: Key }>();
    const keys: Key[] = [];
    let j = i;
    for (; j < all.length; j++) {
      const e = all[j];
      if (e === undefined || !Beats.eq(e.key.t, beat)) break;
      keys.push(e.key);
      const seen = here.get(e.layer);
      if (seen === undefined) here.set(e.layer, { first: e.key, last: e.key });
      else seen.last = e.key;
    }
    i = j;
    const sec = ctx.tempo.secAt(beat);
    let sumIn = 0;
    let sumOut = 0;
    for (const l of layers) {
      const at = here.get(l.layer);
      if (at !== undefined) {
        sumIn += at.first.vIn as number;
        sumOut += at.last.vOut as number;
      } else {
        const v = ctx.engine.channelValue(line, 'alpha', sec, l.layer);
        sumIn += v;
        sumOut += v;
      }
    }
    const value = Math.max(sumIn, sumOut);
    // NaN (a degenerate curve, PH-08) never compares above.
    if (value > ALPHA_MAX) out.push({ beat, value, keys });
  }
  return out;
}

/** One opacity layer: every keyframe value above 255 (negative values hide the line and its notes on purpose). */
function checkSingleLayer(ctx: CheckContext, line: LineId, layer: AlphaLayer): void {
  const over = layer.keys.filter((k) => (k.vIn as number) > ALPHA_MAX || (k.vOut as number) > ALPHA_MAX);
  const first = over[0];
  if (first === undefined) return;
  const ids = over.map((k) => k.id);
  emitProblem(ctx, {
    id: problemId('GP-10', 'line', line),
    message: msg('rules.gp10What', {
      count: over.length,
      line: label(ctx, line),
      beat: fmtBeat(first.t),
      value: fmtNum(Math.max(first.vIn as number, first.vOut as number)),
    }),
    why: msg('rules.gp10Why'),
    at: { line, beat: first.t, keys: ids, tracks: [layer.track.id], field: FIELD.keyValue },
    fixes: [
      fixRef('problems.fix.clampKeyValues', msg('rules.fixClampOpacity'), {
        keys: ids,
        min: -ALPHA_MAX,
        max: ALPHA_MAX,
      }),
      revealFix(msg('rules.fixGoTo'), first.t, { line, keys: ids }),
    ],
  });
}

/**
 * Several opacity layers: the keyframe times at which their total exceeds 255. Clamping single keyframes cannot fix
 * a total, so the fix flattens the layers into one (after which the single-layer clamp applies).
 */
function checkSummedLayers(ctx: CheckContext, line: LineId, layers: readonly AlphaLayer[]): void {
  const over = summedOver(ctx, line, layers);
  const first = over[0];
  if (first === undefined) return;
  const ids: KeyId[] = over.flatMap((o) => o.keys.map((k) => k.id));
  emitProblem(ctx, {
    id: problemId('GP-10', 'line', line),
    message: msg('rules.gp10WhatSum', {
      count: over.length,
      layers: layers.length,
      line: label(ctx, line),
      beat: fmtBeat(first.beat),
      value: fmtNum(first.value),
    }),
    why: msg('rules.gp10WhySum'),
    at: { line, beat: first.beat, keys: ids, tracks: layers.map((l) => l.track.id), field: FIELD.keyValue },
    fixes: [
      navFix('tools.flatten', msg('rules.fixFlatten'), { line, prop: 'alpha' }),
      revealFix(msg('rules.fixGoTo'), first.beat, { line, keys: first.keys.map((k) => k.id) }),
    ],
  });
}

const gp10: CheckRule = {
  id: 'GP-10',
  category: 'gameplay',
  severity: 'warning',
  level: 'guided',
  scope: 'line',
  deps: ['motion', 'tempo'],
  defaultEnabled: true,
  title: msg('rules.gp10Title'),
  check(ctx, only) {
    for (const line of linesToCheck(ctx, only)) {
      const layers = alphaLayers(ctx, line);
      const single = layers.length === 1 ? layers[0] : undefined;
      if (single !== undefined) {
        checkSingleLayer(ctx, line, single);
      } else if (layers.length > 1) {
        checkSummedLayers(ctx, line, layers);
      }
    }
  },
};

const rules: readonly CheckRule[] = [gp01, gp02, gp03, gp04, gp05, gp06, gp07, gp08, gp09, gp10];

export default rules;
